/**
 *
 * DialogBox
 *
 */

import { Input } from 'components/Input'
import React from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import styled, { css, withTheme } from 'styled-components'
import _ from 'lodash'
import { Alert, Form, Icon, Row, NVInput, Progress, T } from '@nv/react-commons/src/Components'
import { faCheck } from '@fa-pro-solid/faCheck'

import { Link } from '../Link'
import { Button } from '../Button'
import { Card } from '../Card'
import { media, Fonts, Colors } from 'themes'
import { faChevronLeft } from '@fa-pro-regular/faChevronLeft'
import { IconButton } from 'components/IconButton'

const widthStyle = css`
  ${media.min.tablet`
    width: 400px;
    margin: auto;
  `};
  ${media.max.tablet`
  width: 343px;
`};
  ${media.max.mobile`
  width: 100%;
  margin: auto 0px;
`};
`
const DivWrapper = ({
  staticContext,
  staticError,
  dismissError,
  action,
  backAction,
  emailAction,
  passwordAction,
  wrappedComponentRef,
  onChange,
  loading,
  validating,
  validated,
  match,
  ...rest
}) => <div {...rest} />
const StyledInModal = styled(DivWrapper)`
  width: 100%;
  margin: auto 0px;
  padding: 0px 8px 16px 8px;
`
const CardWrapper = ({
  staticContext,
  staticError,
  dismissError,
  action,
  backAction,
  emailAction,
  passwordAction,
  wrappedComponentRef,
  onChange,
  loading,
  validating,
  validated,
  match,
  ...rest
}) => <Card {...rest} />
const StyledCard = styled(CardWrapper)`
  && {
    ${widthStyle};
    .ant-card-body {
      padding: 24px 32px 40px 32px;
    }
  }
`

const BackButton = styled(IconButton).attrs({
  type: 'medium',
  color: Colors.pinkishGrey
})`
  && {
    position: absolute;
    top: 18px;
    left: 16px;
    margin: 0;
  }
`

const HeroImg = styled.img.attrs({
  width: 80,
  height: 80
})``

const Title = styled.div`
  text-align: center;
  ${Fonts.style.bold};
  font-size: 18px;
`

const CenteredRow = styled(Row).attrs({
  type: 'flex',
  justify: 'center'
})``

const HeroRow = CenteredRow

const TitleRow = styled(Row).attrs({
  type: 'flex',
  justify: 'center'
})`
  padding: 12px;
`

const CaptionRow = styled(CenteredRow)`
  margin: 8px 18px 16px 18px;
  text-align: center;
  font-size: 15px;
  color: ${Colors.pinkishGrey};
`

const FormItem = styled(Form.Item)`
  & > .ant-form-item-label {
    float: left;
  }
  && {
    margin-bottom: 16px;
    &:last-of-type {
      margin-bottom: 0;
    }
  }
`

const ForgotLink = styled(Link).attrs({
  color: Colors.pinkishGrey
})`
  && {
    float: right;
    font-size: 12px;
    line-height: 24px;
    padding-bottom: 8px;
  }
`

const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: 8px;
`

const SecondaryButton = styled(Button)`
  width: 100%;
`

const ErrorMessage = styled(Alert).attrs({
  type: 'error',
  showIcon: true,
  closable: true
})`
  && {
    position: fixed;
    top: 0;
    margin-top: 8px;
    margin-left: auto;
    margin-right: auto;
    font-size: 12px;
    display: flex;
    flex-direction: row;
    flex-flow: wrap;
    align-items: center;
    ${widthStyle};
    ${media.max.mobile`
      left: 8px;
      right: 8px;
      width: initial;
    `};
    .ant-alert-close-icon {
      line-height: normal;
      top: auto;
    }
    z-index: 10;
  }
`

const StyledIcon = styled(Icon).attrs({
  color: 'inherit'
})`
  margin-right: 4px;
`
const StyledNewPasswordsMatch = styled.span`
  margin-top: 4px;
  font-size: 11px;
  color: ${Colors.emerald};
`

class DialogBox extends React.PureComponent {
  state = { submitted: false }

  UNSAFE_componentWillUnmount () {
    const { dismissError } = this.props
    dismissError && dismissError()
  }

  getValidateStatus = ({ validating, match, validated, submitted }) => {
    if (validating) {
      return 'validating'
    } else if (match) {
      return 'success'
    } else if (validated || submitted) {
      return 'error'
    } else {
      return ''
    }
  }

  handleSubmit = e => {
    e.preventDefault()
    this.setState({ submitted: true })
    this.props.form.validateFields((err, values) => {
      if (!err) {
        this.props.action(values)
      }
    })
  }

  render () {
    const { getFieldDecorator } = this.props.form
    const {
      messages,
      error,
      secondaryAction,
      inModal,
      emailAction,
      passwordAction,
      backAction,
      loading,
      validating,
      validated,
      match,
      theme: { mascotImg },
      ...props
    } = this.props
    const { submitted } = this.state
    const Container = inModal ? StyledInModal : StyledCard

    return (
      <>
        {error && <ErrorMessage message={<T id={error.code ? 'login_failed' : 'error'} />} />}
        <Container {...props}>
          {backAction && <BackButton icon={faChevronLeft} onClick={backAction} />}
          <HeroRow>
            <HeroImg src={mascotImg} />
          </HeroRow>
          <TitleRow>
            <Title>
              <T id={messages.title} />
            </Title>
          </TitleRow>
          {_.isNumber(messages.percent) && (
            <CenteredRow>
              <Progress spin width={46} strokeWidth={8} />
            </CenteredRow>
          )}
          {messages.captions &&
            messages.captions.map((caption, i) => (
              <CaptionRow key={i}>
                {_.isObject(caption) && <T id={caption.id} values={caption.values} />}
                {!_.isObject(caption) && <T id={caption} />}
              </CaptionRow>
            ))}
          <Form onSubmit={this.handleSubmit} layout='vertical'>
            {messages.email && (
              <FormItem label={<T id={messages.email} />}>
                <ForgotLink onClick={emailAction} data-analyticsid='confirmForgotEmail'>
                  <T id='forgot' defaultMessage='I forgot' />
                </ForgotLink>
                {getFieldDecorator('email', {
                  rules: [
                    { required: true, whitespace: true },
                    {
                      type: 'email'
                    }
                  ]
                })(<NVInput />)}
              </FormItem>
            )}
            {messages.password && (
              <FormItem label={<T id={messages.password} />}>
                <ForgotLink onClick={passwordAction} data-analyticsid='confirmForgotPassword'>
                  <T id='forgot' defaultMessage='I forgot' />
                </ForgotLink>
                {getFieldDecorator('password', {
                  rules: [
                    { required: true, whitespace: true },
                    {
                      min: 6
                    }
                  ]
                })(<Input type='password' />)}
              </FormItem>
            )}
            {messages.newPassword && (
              <FormItem label={<T id={messages.newPassword} />}>
                {getFieldDecorator('newPassword', {
                  rules: [{ required: true, whitespace: true }]
                })(<Input type='password' />)}
              </FormItem>
            )}
            {messages.newPasswordRepeat && (
              <FormItem
                label={<T id={messages.newPasswordRepeat} />}
                validateStatus={this.getValidateStatus({ validating, match, validated, submitted })}
              >
                {getFieldDecorator('newPasswordRepeat', {
                  rules: [{ required: true, whitespace: true }]
                })(<Input type='password' />)}
                {!validating && match && (
                  <StyledNewPasswordsMatch>
                    <StyledIcon icon={faCheck} />
                    <T id='new_password_repeat_match' />
                  </StyledNewPasswordsMatch>
                )}
              </FormItem>
            )}
            {messages.submit && (
              <FormItem>
                <SubmitButton type='primary' htmlType='submit' data-analyticsid={messages.submit} loading={loading}>
                  <T id={messages.submit} allCaps />
                </SubmitButton>
              </FormItem>
            )}
            {messages.secondary && (
              <FormItem>
                <SecondaryButton onClick={() => secondaryAction(this.props.data)} data-analyticsid={messages.secondary}>
                  <T id={messages.secondary} allCaps />
                </SecondaryButton>
              </FormItem>
            )}
          </Form>
        </Container>
      </>
    )
  }
}

DialogBox.propTypes = {
  action: PropTypes.func,
  backAction: PropTypes.func,
  data: PropTypes.object,
  dismissError: PropTypes.func,
  emailAction: PropTypes.func,
  error: PropTypes.object,
  form: PropTypes.object,
  inModal: PropTypes.bool,
  loading: PropTypes.bool,
  match: PropTypes.object,
  messages: PropTypes.object,
  passwordAction: PropTypes.func,
  secondaryAction: PropTypes.func,
  validated: PropTypes.bool,
  validating: PropTypes.bool,
  theme: PropTypes.object
}

DialogBox.defaultProps = {
  messages: {
    title: 'login',
    email: 'email',
    password: 'password',
    submit: 'login'
  }
}

const WrappedDialogBox = compose(
  withTheme,
  Form.create({
    mapPropsToFields: props => {
      return _.mapValues(props.values, value => Form.createFormField({ value }))
    },
    onValuesChange: (props, values) => {
      props.onChange(values)
    }
  })
)(DialogBox)

export { WrappedDialogBox as DialogBox }

DivWrapper.propTypes = {
  action: PropTypes.string,
  backAction: PropTypes.func,
  dismissError: PropTypes.func,
  emailAction: PropTypes.func,
  loading: PropTypes.bool,
  match: PropTypes.object,
  onChange: PropTypes.func,
  passwordAction: PropTypes.func,
  staticContext: PropTypes.object,
  staticError: PropTypes.object,
  validated: PropTypes.bool,
  validating: PropTypes.bool,
  wrappedComponentRef: PropTypes.func
}

CardWrapper.propTypes = {
  action: PropTypes.func,
  backAction: PropTypes.func,
  dismissError: PropTypes.func,
  emailAction: PropTypes.func,
  loading: PropTypes.bool,
  match: PropTypes.object,
  onChange: PropTypes.func,
  passwordAction: PropTypes.func,
  staticContext: PropTypes.object,
  staticError: PropTypes.object,
  validated: PropTypes.bool,
  validating: PropTypes.bool,
  wrappedComponentRef: PropTypes.func
}
